import { get } from 'lodash-es';
import { isEmptyString } from '@/utils/string';

type ErrorWithBody = {
  body?: unknown;
};

type ErrorWithResponse = {
  response?: {
    body?: unknown;
    status?: unknown;
  };
};

type ErrorWithStatus = {
  status?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object';
}

export function getApiClientErrorBody(error: unknown): unknown {
  if (!isRecord(error)) return undefined;

  if ('body' in error) {
    return (error as ErrorWithBody).body;
  }

  if ('response' in error) {
    const response = (error as ErrorWithResponse).response;
    if (response != null && typeof response === 'object' && 'body' in response) {
      return response.body;
    }
  }

  return undefined;
}

export function getApiClientErrorStatus(error: unknown): number | undefined {
  if (!isRecord(error)) return undefined;

  const status = (error as ErrorWithStatus).status;
  if (typeof status === 'number') return status;

  const responseStatus = (error as ErrorWithResponse).response?.status;
  return typeof responseStatus === 'number' ? responseStatus : undefined;
}

export function isApiClientError(error: unknown): boolean {
  return getApiClientErrorStatus(error) !== undefined;
}

function nonBlankDetail(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }
  const detail = String(value);
  return isEmptyString(detail) ? undefined : detail;
}

/**
 * The server-supplied detail from an error body, or `undefined` when there is
 * none. A blank detail counts as none: returning `''` would leave callers
 * rendering an empty error snackbar instead of falling back to their own copy.
 */
export function getApiClientErrorDetail(body: unknown): string | undefined {
  return nonBlankDetail(get(body, 'message')) ?? nonBlankDetail(get(body, 'errors[0].detail'));
}

export function formatApiClientErrorMessage(body: unknown, status: number): string {
  return getApiClientErrorDetail(body) ?? `An unexpected error occurred with HTTP code ${status}`;
}

/**
 * Whether a status's server-supplied detail is written for the user, and so is
 * worth showing instead of the caller's own copy.
 *
 * `ui_api` attaches a detail to *every* error response, but most of them are
 * internal (`services/ui_api/serve/format.go`): 5xx reads
 * `internal error (reference …)`, 404 `not found (reference …)`, 401
 * `unauthorized`. Preferring those would replace copy like "Something went wrong
 * when authenticating with your Okta account" with a reference string.
 *
 * Only the 4xx statuses that carry a request-specific explanation qualify — 400
 * (`BadRequestWithMessage`), 409 conflicts, 422 validation.
 */
function hasActionableDetail(status: number): boolean {
  return status >= 400 && status < 500 && status !== 401 && status !== 404;
}

/**
 * The server-supplied detail for an API error, or `undefined` when the response
 * carried none — or when its detail is one of the internal strings described in
 * `hasActionableDetail`. Unlike `getApiClientErrorMessageFromError` this never
 * invents a generic "HTTP code N" sentence, so callers can fall back to their
 * own copy.
 */
export function getApiClientErrorDetailFromError(error: unknown): string | undefined {
  const status = getApiClientErrorStatus(error);
  if (status === undefined || !hasActionableDetail(status)) return undefined;
  return getApiClientErrorDetail(getApiClientErrorBody(error));
}

export function getApiClientErrorMessageFromError(error: unknown): string | undefined {
  const status = getApiClientErrorStatus(error);
  if (status === undefined) return undefined;
  return formatApiClientErrorMessage(getApiClientErrorBody(error), status);
}
